import { Badge, FloatingIndicator, Group, Stack, Tabs } from '@mantine/core';
import { IconScale } from '@tabler/icons-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useSearchParams } from 'react-router-dom';

import { GovernanceComingSoonSection } from 'src/Containers/Admin/GovernanceComingSoonSection';
import { GovernanceSection } from 'src/Containers/Admin/GovernanceSection';
import { HouseholdsSection } from 'src/Containers/Admin/HouseholdsSection';
import { MyRightsSection } from 'src/Containers/Admin/MyRightsSection';
import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import { trackAdminTabChanged } from 'src/Services/Mixpanel/events';
import { AssetGovernanceFields } from 'src/types/governance.types';
import { getStructureVocabulary, LEGAL_STRUCTURE_LABELS } from 'src/Utils/legalStructureVocabulary';

import styles from './AdminPage.module.scss';
import AdvancedSection from './sections/AdvancedSection.component';
import AssetSettingsSection from './sections/AssetSettingsSection.component';
import MembersSection from './sections/MembersSection.component';
import StructureSection from './sections/StructureSection.component';

type TabKey = 'membres' | 'le-bien' | 'gouvernance' | 'structure' | 'avance';

type TabConfig = {
  key: TabKey;
  label: string;
  visible: boolean;
  badge?: string;
};

const AdminPage = () => {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const controlsRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const { data: asset } = useFetchAssetDetails();
  const isOwner = asset?.isOwner ?? false;
  const governance = asset as unknown as AssetGovernanceFields | undefined;
  const currentUserOwnershipNature = governance?.currentUserOwnershipNature;
  const legalStructure = governance?.legalStructure;
  const vocabulary = getStructureVocabulary(t, legalStructure);

  const [searchParams, setSearchParams] = useSearchParams();

  const tabs: TabConfig[] = [
    {
      key: 'membres',
      label: vocabulary.membersTabLabel,
      visible: true,
    },
    {
      key: 'gouvernance',
      label: t('admin.tabs.governance', 'Gouvernance'),
      visible: isOwner || !!currentUserOwnershipNature,
    },
    {
      key: 'le-bien',
      label: t('admin.tabs.asset', 'Le bien'),
      visible: isOwner,
    },
    {
      key: 'structure',
      label: t('admin.tabs.structure', 'Structure du bien'),
      visible: true,
    },
    {
      key: 'avance',
      label: t('admin.tabs.advanced', 'Avancé'),
      visible: isOwner,
    },
  ];

  const visibleTabs = tabs.filter(tab => tab.visible);
  const rawUrlTab = searchParams.get('tab');
  const urlTab = (rawUrlTab === 'foyers' ? 'membres' : rawUrlTab) as TabKey | null;
  const fallbackTab = visibleTabs[0]?.key ?? 'membres';
  const activeTab: TabKey =
    urlTab && visibleTabs.some(tab => tab.key === urlTab) ? urlTab : fallbackTab;

  const handleTabChange = (value: string | null) => {
    if (!value) return;
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', value);
    setSearchParams(newParams, { replace: true });
    trackAdminTabChanged({ tab: value });
  };

  return (
    <div className={styles.container}>
      {legalStructure && (
        <Group mb="md">
          <Badge size="lg" variant="filled" color="teal" leftSection={<IconScale size={14} />}>
            {LEGAL_STRUCTURE_LABELS[legalStructure]}
          </Badge>
        </Group>
      )}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        className={styles.tabs}
        keepMounted={false}
      >
        <Tabs.List ref={rootRef} className={styles.tabsList}>
          {visibleTabs.map(tab => (
            <Tabs.Tab
              key={tab.key}
              value={tab.key}
              ref={node => (controlsRefs.current[tab.key] = node)}
              className={styles.tab}
            >
              <Group gap="xs" wrap="nowrap" justify="center">
                <span>{tab.label}</span>
                {tab.badge && (
                  <Badge size="xs" variant="light" color="gray">
                    {tab.badge}
                  </Badge>
                )}
              </Group>
            </Tabs.Tab>
          ))}

          <FloatingIndicator
            parent={rootRef.current}
            target={activeTab ? controlsRefs.current[activeTab] : null}
            className={styles.indicator}
          />
        </Tabs.List>

        <Tabs.Panel value="membres" className={styles.panel}>
          <Stack gap="xl">
            <MembersSection />
            <HouseholdsSection />
          </Stack>
        </Tabs.Panel>
        {isOwner && (
          <Tabs.Panel value="le-bien" className={styles.panel}>
            <AssetSettingsSection />
          </Tabs.Panel>
        )}
        {(isOwner || !!currentUserOwnershipNature) && (
          <Tabs.Panel value="gouvernance" className={styles.panel}>
            <Stack gap="xl">
              {currentUserOwnershipNature && <MyRightsSection />}
              {isOwner && <GovernanceSection />}
              {isOwner && <GovernanceComingSoonSection />}
            </Stack>
          </Tabs.Panel>
        )}
        <Tabs.Panel value="structure" className={styles.panel}>
          <StructureSection />
        </Tabs.Panel>
        {isOwner && (
          <Tabs.Panel value="avance" className={styles.panel}>
            <AdvancedSection />
          </Tabs.Panel>
        )}
      </Tabs>
      <Outlet />
    </div>
  );
};

export default AdminPage;
